import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { rgba, ellipsis } from 'polished'
import {
  Button,
  Inline,
  ListItem,
  Badge,
  Section,
  Modal,
  ModalHeader,
  ModalBody,
} from 'chaoskit/src/components'
import { link } from 'chaoskit/src/assets/styles/utility'
import Container from 'chaoskit/src/components/Container'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import { HeroTitle, HeroContent, HeroTitleSub } from '~partials/Hero'
import HomeOverview from '~partials/home/HomeOverview'
import CustomerQuote from '~partials/home/CustomerQuote'
import SafeSimple from '~partials/home/SafeSimple'
import CustomerLogos from '~partials/global/CustomerLogos'
import { ReactComponent as HeroSVG } from '~media/home/hero.svg'
import Icon from '~components/Icon'
import { ReactComponent as IntroGremlinSVG } from '~media/home/intro-gremlin.svg'
import { ReactComponent as DownloadGuideSVG } from '~media/home/download-guide.svg'
import { ReactComponent as WeeklyLiveDemoSVG } from '~media/home/weekly-live-demo.svg'
import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import HelmetSEO from '~partials/global/HelmetSEO'
import MarketoForm from '~components/MarketoForm'
import track from '~helpers/track'
import MutinyTag from '~components/MutintyTag'
import {
  HomeHero,
  HomeHeroGrid,
  HomeHeroGridContent,
  HomeHeroGridImage,
} from '~partials/home/Hero'

const HomeHeroAnnouncement = ({
  announcement: { style, badge, text, link: announcementLink },
}) => {
  const theme = useTheme()

  return (
    <TextLink
      href={announcementLink.url}
      css={[
        link.contrast(theme),
        {
          boxShadow: theme.boxShadow.base,
          paddingTop: theme.space.xsmall,
          paddingBottom: theme.space.xsmall,
          paddingLeft: theme.space.xsmall,
          paddingRight: theme.space.small,
          ...theme.text.xsmall,
          display: 'inline-grid',
          gridTemplateColumns: 'auto 1fr',
          gap: theme.space.small,
          alignItems: 'center',
          borderRadius: (theme.height.small + theme.space.xsmall) / 2,
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
          transformOrigin: 'center center',

          '&:hover, &:focus': {
            transform: 'scale(1.025)',
          },

          [theme.mq.small__max]: {
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',

            '&:hover, &:focus': {
              transform: 'scale(1.025) translateX(-50%)',
            },
          },
        },

        style === 'orange' && {
          background: theme.color.dark.overlay,
        },

        style === 'green' && {
          background: rgba(theme.contrast.base, 0.2),
        },
      ]}
    >
      {badge && (
        <Badge
          type={style === 'orange' ? 'warning' : 'primary'}
          rounded
          css={{
            cursor: 'pointer', // Resets default cursor style to avoid UX confusion
          }}
        >
          {badge}
        </Badge>
      )}
      <div css={[ellipsis()]}>
        <Data>{text}</Data>
        <Icon
          icon="arrow-right"
          css={{
            top: 'auto', // Resets some default offset we normally have for inline text since the grid is taking care of that
            marginLeft: theme.space.small,
          }}
        />
      </div>
    </TextLink>
  )
}

HomeHeroAnnouncement.propTypes = {
  announcement: PropTypes.object.isRequired,
}

const CrashCourse = ({ crashCourse }) => {
  const theme = useTheme()

  const [isWhitePaperModalOpen, setWhitePaperModalOpen] = useState(false)

  const gremlinHeight = 50

  const formId = 1008

  return (
    <Section
      css={{ background: theme.brand.teal, position: 'relative' }}
      className="u-contrast"
    >
      <IntroGremlinSVG
        css={{
          height: gremlinHeight,
          position: 'absolute',
          top: -gremlinHeight + 12, // Offset for illustration placement only
          right: theme.space.xlarge,
        }}
      />
      <Container>
        <div
          css={{
            display: 'grid',
            gap: theme.space.base,

            [theme.mq.medium]: {
              gridTemplateColumns: '1fr 1.25fr',
              gap: theme.space.xlarge,
            },
          }}
        >
          <h2
            css={{
              textAlign: 'center',
              margin: 0,

              [theme.mq.medium]: {
                textAlign: 'left',
              },
            }}
          >
            <Data>{crashCourse.heading}</Data>
          </h2>
          <div
            css={{
              display: 'grid',
              gap: theme.space.base,

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: theme.space.large,
              },
            }}
          >
            <div
              css={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gap: theme.space.base,
              }}
            >
              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: theme.space.base,
                  ...theme.text.small,
                }}
              >
                <WeeklyLiveDemoSVG css={{ height: theme.height.small }} />
                <p css={{ margin: 0 }}>
                  <Data>{crashCourse.webinar.summary}</Data>
                </p>
              </div>
              <ButtonLink
                type="default"
                css={{ width: '100%' }}
                url={crashCourse.webinar.cta.url}
              >
                <Data>{crashCourse.webinar.cta.label}</Data>
              </ButtonLink>
            </div>
            <div
              css={{
                display: 'grid',
                gridTemplateRows: '1fr auto',
                gap: theme.space.base,
              }}
            >
              <div
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: theme.space.base,
                  ...theme.text.small,
                }}
              >
                <DownloadGuideSVG css={{ height: theme.height.small }} />

                <p css={{ margin: 0 }}>
                  <Data>{crashCourse.guide.summary}</Data>
                </p>
              </div>
              <Button
                type="default"
                css={{ width: '100%' }}
                onClick={() => setWhitePaperModalOpen(true)}
              >
                <Data>{crashCourse.guide.cta.label}</Data>
              </Button>
              <Modal
                open={isWhitePaperModalOpen}
                setIsOpen={setWhitePaperModalOpen}
              >
                <ModalHeader
                  centered
                  title="Learn the basics of Chaos Engineering"
                  setIsOpen={setWhitePaperModalOpen}
                />
                <ModalBody css={{ paddingTop: 0 }}>
                  <p>
                    <Data>{crashCourse.guide.summary}</Data>
                  </p>
                  <MarketoForm
                    css={{ marginTop: theme.space.base }}
                    id={formId}
                    fieldValues={{ programID: 'PG1069A1' }}
                    thankYouMessage={`<strong>Thanks for requesting <em>Chaos Engineering: Finding Failures Before They Become Outages</em>! </strong><a href="https://chaos.gremlin.com/rs/251-JGH-155/images/2019-11-Chaos_Engineering_Whitepaper.pdf">View the whitepaper here.</a> (A copy has also been sent to your email.)`}
                    onSuccess={() => {
                      track('Form submitted', {
                        label: `marketo-${formId}`,
                      })
                    }}
                  />
                </ModalBody>
              </Modal>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

CrashCourse.propTypes = {
  crashCourse: PropTypes.object.isRequired,
}

const Home = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      headerProps={{ hideAnnouncementBar: pageData.hero.announcement.show }}
      footerProps={{ hideCTA: true }}
    >
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <MutinyTag />
      <HomeHero>
        <HeroContent>
          <HomeHeroGrid>
            <HomeHeroGridImage>
              <HeroSVG />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              {pageData.hero.announcement.show && (
                <HomeHeroAnnouncement
                  announcement={pageData.hero.announcement}
                />
              )}
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
              <div css={{ marginTop: theme.space.large }}>
                <Inline
                  css={{
                    justifyContent: 'center',

                    [theme.mq.medium]: {
                      justifyContent: 'flex-start',
                    },
                  }}
                >
                  <ListItem>
                    <ButtonLink
                      noContrast
                      type="primary"
                      url="/get-started/?ref=hero"
                    >
                      Get started
                    </ButtonLink>
                  </ListItem>
                  <ListItem>
                    <ButtonLink
                      type="default"
                      url="/weekly-live-demo/?ref=homepage"
                    >
                      Watch demo
                    </ButtonLink>
                  </ListItem>
                </Inline>
              </div>
            </HomeHeroGridContent>
          </HomeHeroGrid>
          <CustomerLogos
            contrast
            css={{
              paddingTop: `${theme.space.large}px !important`,
            }}
          />
        </HeroContent>
      </HomeHero>
      <main css={{ position: 'relative', zIndex: 1 }}>
        <CrashCourse crashCourse={pageData.crashCourse} />
        <HomeOverview hideFirstSeparator />
        <CustomerQuote />
        <SafeSimple />
      </main>
    </BaseLayout>
  )
}

Home.propTypes = {
  data: PropTypes.object.isRequired,
}

export const pageQuery = graphql`
  query HomePageData {
    file(name: { eq: "home" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            announcement {
              show
              style
              badge
              text
              link {
                url
              }
            }
            blurb
            heading
          }
          crashCourse {
            heading
            webinar {
              cta {
                label
                url
              }
              summary
            }
            guide {
              cta {
                label
              }
              summary
            }
          }
        }
      }
    }
  }
`

export default Home
